import { upperFirst } from 'lodash';
import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import { DateValue } from 'react-aria';

import { RectSize } from './types';
import { MIN_RECT_SIZE } from './constants';
import { Rect } from '../graphql/__generated__/graphql';

const macRegex = /mac/i;

export const isMac = macRegex.test(navigator.platform);

export const titleCase = (val: string): string => upperFirst(val.toLowerCase());

export const toLocalDate = (date: CalendarDate | DateValue): Date => (
  date.toDate(getLocalTimeZone())
);

/**
 * Return all keys (recursive) from an object in an array format.
 * @param obj
 */
export const getObjectKeys = (
  // eslint-disable-next-line @typescript-eslint/ban-types
  obj: object,
): string[] => {
  const currKeys: string[] = Object.keys(obj);

  const valueKeys = Object.values(obj).reduce((prev: string[], curr) => {
    if (typeof curr === 'object' && !Array.isArray(curr)) {
      return prev.concat(getObjectKeys(curr));
    }

    return prev;
  }, []);

  return currKeys.concat(valueKeys);
};

/**
 * Calculates a minimum visible size for a dimension based on zoom level. Will scale a small
 * element to be at least MIN_RECT_SIZE[dimension].
 * @param rect
 * @param dim
 * @param zoom
 */
export const getDimVisibleSize = (rect: RectSize, dim: keyof RectSize, zoom = 1) => {
  const zoomedSize = rect[dim] * zoom;
  if (zoomedSize > MIN_RECT_SIZE[dim] || zoom >= 2) {
    return rect[dim];
  }

  const minimumSize = MIN_RECT_SIZE[dim] / zoom;
  return zoomedSize > minimumSize ? zoomedSize : minimumSize;
};

/**
 * Get the modified size of a Rect which fits with our minimum size requirements on screen.
 * @param rect
 * @param zoom
 */
export const getRectSize = (rect: Rect, zoom?: number): Rect => {
  // Resize to make larger / more visible when zoomed far out.
  const rectVisibleSize = {
    height: getDimVisibleSize(rect, 'height', zoom),
    width: getDimVisibleSize(rect, 'width', zoom),
  };

  // Adjust x & y positioning if we've resized the element
  return {
    ...rectVisibleSize,
    x: rect.width < rectVisibleSize.width ? (
      rect.x - (rectVisibleSize.width - rect.width) / 2
    ) : rect.x,
    y: rect.height < rectVisibleSize.height ? (
      rect.y - (rectVisibleSize.height - rect.height) / 2
    ) : rect.y,
  };
};
